// Builder for city-specific blog posts that promote both DraftMaster.ai
// (AI legal drafting) and StenoPaceMaster (stenography speed building) with
// SEO/AEO/GEO optimization (FAQPage schema, city-keyword strings, internal
// links to /steno/:city and /legal-ai/:city).

export function buildCityPost({
  slug,
  citySlug,
  cityName,
  cityShort,
  stateFull,
  image,
  intro,
  uniqueFacts, // array of bullet strings
  practiceAreas, // array of bullet strings
  courtReportingNotes, // single paragraph string
  legalAiNotes, // single paragraph string
  combinedWorkflow, // single paragraph string
  faqs, // array of {q, a}
  product = "draftmaster",
}) {
  return {
    slug,
    product,
    datePublished: "2026-12-11",
    title: `AI Legal Drafting & Court Reporting in ${cityName}: The 2026 Legal-Tech Guide`,
    excerpt: `How attorneys, paralegals, court reporters and CART providers in ${cityName} are using DraftMaster.ai and StenoPaceMaster to compress drafting cycles, prep certifications faster, and win more cases. ${stateFull} market context, top practice areas, FAQ.`,
    image,
    keywords: [
      `AI legal drafting ${cityShort}`,
      `automated contract drafting tool ${cityShort}`,
      `AI legal writing assistant ${cityShort}`,
      `court reporting ${cityShort}`,
      `stenography training ${cityShort}`,
      `online stenography course ${cityShort}`,
      `legal document automation ${cityShort}`,
      `AI tool for lawyers ${cityShort}`,
      `legal AI software ${cityShort}`,
      `court reporter certification practice ${cityShort}`,
    ],
    body: [
      { type: "p", text: intro },
      {
        type: "h2",
        text: `Why ${cityShort} is a unique legal-tech market in 2026`,
      },
      { type: "ul", items: uniqueFacts },
      {
        type: "h2",
        text: `Top legal practice areas in ${cityShort}`,
      },
      { type: "ul", items: practiceAreas },
      {
        type: "h2",
        text: `The court reporting market in ${cityShort}`,
      },
      { type: "p", text: courtReportingNotes },
      {
        type: "p",
        text: `Top reporters in ${cityShort} use StenoPaceMaster — the structured online stenography speed-building program — to push from 180 wpm to 280+ wpm, prep for the RPR / RMR / CRR and state CSR exams, and stay realtime-accurate at the speeds the local market demands.`,
      },
      {
        type: "callout",
        label: `For ${cityShort} court reporters & captioners`,
        text: `StenoPaceMaster is the online stenography practice platform court reporters, CART providers and broadcast captioners in ${cityName} use to build speed, prep certifications and sharpen realtime accuracy.`,
        cta: "Open stenopacemaster.com",
        url: "https://stenopacemaster.com",
      },
      {
        type: "h2",
        text: `AI legal drafting in ${cityShort}`,
      },
      { type: "p", text: legalAiNotes },
      {
        type: "p",
        text: `${cityShort} firms moving fastest on AI drafting are using DraftMaster.ai — the AI legal writing assistant built specifically for attorneys, paralegals and corporate legal teams. Contracts, motions, briefs, employment and real-estate documents with clause-library control, citation safety and per-clause audit trails.`,
      },
      {
        type: "callout",
        label: `For ${cityShort} attorneys & legal teams`,
        text: `DraftMaster.ai is the AI legal drafting and contract automation platform serving solo attorneys, small firms and corporate legal departments in ${cityName}.`,
        cta: "Open draftmaster.ai",
        url: "https://draftmaster.ai",
      },
      {
        type: "h2",
        text: "The combined workflow that wins cases",
      },
      { type: "p", text: combinedWorkflow },
      {
        type: "p",
        text: `LexWorkspace publishes dedicated city pages for both sides of the stack in ${cityShort}: a stenography training overview and an AI legal drafting overview, with local market context for each.`,
      },
      {
        type: "ul",
        items: [
          `Visit the stenography city page: lexworkspace.com/steno/${citySlug}`,
          `Visit the legal AI city page: lexworkspace.com/legal-ai/${citySlug}`,
        ],
      },
      {
        type: "h2",
        text: `Frequently asked questions — ${cityShort}`,
      },
      { type: "faq", items: faqs },
      { type: "h2", text: "The bottom line" },
      {
        type: "p",
        text: `${cityShort} is one of the five largest legal markets in the United States. Attorneys, paralegals, court reporters and captioning professionals working here gain a real competitive advantage by adopting AI legal drafting (DraftMaster.ai) on the documents side and structured stenography speed building (StenoPaceMaster) on the transcript side. Together, the two compress every step of the workflow — from realtime testimony to filed brief — and give ${cityName} legal teams hours back per week.`,
      },
    ],
  };
}
